import {
  SCHEMA_VERSION,
  type CampaignDraft,
  type CampaignDraftContent,
  type CampaignDraftData,
} from "src/services/CampaignDraftTypes";
import type { CampaignData } from "../../EngagementInterfaces";
import type { EmailDesign, GeneratedTemplate } from "../Tabs/EmailCampaign/AiEmail/AiCampaignTypes";
import { parseGeneratedDesign } from "../Tabs/EmailCampaign/AiEmail/AiCampaignUtils";
import {
  AI_EMAIL_EDITOR_ROUTE,
  COMPANY_NAME_STORAGE_KEY,
  TEMPLATE_EMAIL_EDITOR_ROUTE,
} from "../CampaignSetup/CampaignSetupConstants";

export interface DraftLocationState {
  campaignData?: Partial<CampaignData>;
  draft?: CampaignDraft;
}

export const readString = (value: unknown): string => (typeof value === "string" ? value : "");

export const readStringList = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((item): item is string => typeof item === "string") : [];

/** `current_step` is 1-based on the backend; wizards index their steps from 0. */
export const toStepIndex = (data: CampaignDraftData, stepCount: number): number => {
  const step = typeof data.current_step === "number" ? data.current_step : 1;
  return Math.min(Math.max(step - 1, 0), stepCount - 1);
};

export const toCurrentStep = (stepIndex: number): number => stepIndex + 1;

export const draftToCampaignData = (draft: CampaignDraft): Partial<CampaignData> => ({
  channel: draft.channel,
  campaignName: readString(draft.data.campaign_name),
  sendTo: readStringList(draft.data.send_to),
  excludeList: readStringList(draft.data.dont_send_to),
  companyNameEngagements: localStorage.getItem(COMPANY_NAME_STORAGE_KEY) ?? "",
});

export const draftContentToTemplate = (
  content: CampaignDraft["content"] | undefined
): GeneratedTemplate | null => {
  const design = parseGeneratedDesign(content?.design);
  const html = readString(content?.html);
  return design || html ? { design, html } : null;
};

export const templateToDraftContent = (
  template: GeneratedTemplate | null,
  design?: EmailDesign | null
): CampaignDraftContent | undefined =>
  template ? { design: design ?? template.design, html: template.html } : undefined;

export const getDraftEditorRoute = (draft: CampaignDraft): string => {
  return draft.data.wizard === "template" ? TEMPLATE_EMAIL_EDITOR_ROUTE : AI_EMAIL_EDITOR_ROUTE;
};

export const getDraftWarningOutcome = (warnings: CampaignDraft["warnings"] = []) => ({
  missingLists: warnings.flatMap(warning => (warning.code === "missing_lists" ? warning.lists : [])),
  isNameTaken: warnings.some(warning => warning.code === "name_taken"),
});

export const withoutLists = (names: string[], missing: string[]): string[] =>
  names.filter(name => !missing.includes(name));

interface SetupDraftState {
  isAIGeneratorOn: boolean;
  campaignName: string;
  sendTo: string[];
  excludeList: string[];
}

/** A draft saved before the wizard proper starts: only the first step's fields are known. */
export const buildSetupDraftData = ({
  isAIGeneratorOn,
  campaignName,
  sendTo,
  excludeList,
}: SetupDraftState): CampaignDraftData => {
  return {
    schema_version: SCHEMA_VERSION,
    wizard: isAIGeneratorOn ? "ai" : "template",
    current_step: 1,
    campaign_name: campaignName.trim(),
    send_to: sendTo,
    dont_send_to: excludeList,
  };
};
